import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { AppState } from '../types';
import { 
  getTokenFromURL, 
  validateToken, 
  extractUserFromToken, 
  clearTokenFromURL,
  JWTError 
} from '../utils/jwt';

interface AuthStore extends AppState {
  initialize: () => Promise<void>;
  setToken: (token: string) => void;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  subscribeWithSelector((set, get) => ({
    isAuthenticated: false,
    user: null,
    token: null,
    isLoading: true,
    error: null,

    initialize: async () => {
      try {
        // Prevent re-initialization if already authenticated
        const currentState = get();
        if (currentState.isAuthenticated && currentState.token) {
          if (import.meta.env.DEV) {
            console.log('🔄 Already authenticated, skipping initialization');
          }
          return;
        }

        set({ isLoading: true, error: null });

        let token = getTokenFromURL();
        
        // If no token in URL, check sessionStorage
        if (!token) {
          token = sessionStorage.getItem('auth_token');
        }
        
        if (import.meta.env.DEV) {
          console.log('🚀 Auth initialization:', { 
            tokenFromURL: !!getTokenFromURL(),
            tokenFromSession: !!sessionStorage.getItem('auth_token'),
            finalTokenFound: !!token 
          });
        }
        
        if (!token) {
          if (import.meta.env.DEV) {
            console.error('❌ No token found in URL or sessionStorage');
          }
          set({ 
            isLoading: false, 
            error: 'No authentication token found. Please close and reopen the app.',
            isAuthenticated: false
          });
          return;
        }

        // Validate token
        validateToken(token);
        
        // Extract user information
        const user = extractUserFromToken(token);
        
        if (import.meta.env.DEV) {
          console.log('✅ Token validated successfully:', { 
            userId: user.id, 
            userType: user.userType, 
            flatId: user.activeFlatId 
          });
        }
        
        // Store token in sessionStorage for persistence across refreshes
        sessionStorage.setItem('auth_token', token);
        
        // Clear token from URL for security (after validation and storage)
        clearTokenFromURL();
        
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });

      } catch (error) {
        let errorMessage = 'Authentication failed';
        
        if (error instanceof JWTError) {
          switch (error.code) {
            case 'TOKEN_EXPIRED':
              errorMessage = 'Your session has expired. Please close and reopen the app.';
              break;
            case 'INVALID_TOKEN':
            case 'MALFORMED_TOKEN':
              errorMessage = 'Invalid authentication token. Please close and reopen the app.';
              break;
            default:
              errorMessage = error.message;
          }
        }

        set({
          isLoading: false,
          error: errorMessage,
          isAuthenticated: false,
          token: null,
          user: null
        });
      }
    },

    setToken: (token: string) => {
      try {
        validateToken(token);
        const user = extractUserFromToken(token);
        
        set({
          token,
          user,
          isAuthenticated: true,
          error: null
        });
      } catch (error) {
        set({
          error: error instanceof JWTError ? error.message : 'Invalid token',
          isAuthenticated: false,
          token: null,
          user: null
        });
      }
    },

    logout: () => {
      // Clear token from sessionStorage
      sessionStorage.removeItem('auth_token');
      
      set({
        isAuthenticated: false,
        user: null,
        token: null,
        error: 'Please close and reopen the app to continue.'
      });
    },

    clearError: () => {
      set({ error: null });
    }
  }))
);